import type { ChartType, ColumnDefinition, DataRow } from "@compass/shared/schema/chart";
import { useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { Button } from "components/elements";
import { trpc } from "lib/trpc";
import {
    ChartPreviewCard,
    DetailsFormCard,
    ExpandedPreviewOverlay,
    InputsCard,
    type PermissionSummaryEntry,
    PermissionsSummaryCard,
} from "./ChartEditorComponents";
import { ChartPermissionPicker, type PermissionEntry } from "./ChartPermissionPicker";
import { useExternalOrgConfirm } from "./useExternalOrgConfirm";

export const CreateChartScreen = () => {
    const navigate = useNavigate();
    const createMutation = trpc.charts.create.useMutation();
    const saveDraftMutation = trpc.charts.saveDraft.useMutation();
    const commitMutation = trpc.charts.commitVersion.useMutation();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [columns, setColumns] = useState<ColumnDefinition[]>([]);
    const [rows, setRows] = useState<DataRow[]>([]);
    const [chartType, setChartType] = useState<ChartType>("LINE");
    const [isCreating, setIsCreating] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);
    const [expandedPreview, setExpandedPreview] = useState(false);
    const [permissions, setPermissions] = useState<PermissionEntry[]>([
        { target: "COMPASS_TEAM", targetId: 0, label: "Compass Team", role: "VIEWER" },
    ]);

    const { onBeforeAdd: handleBeforeAdd, modal: externalOrgConfirmModal } = useExternalOrgConfirm({
        existingEntries: permissions,
    });

    const downArray = (
        <svg
            className="inline-block"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
            <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
    );

    const hasData = columns.length > 1 && rows.length > 0;

    const permissionEntries = useMemo<PermissionSummaryEntry[]>(
        () =>
            permissions.map((p) => ({
                key: `${p.target}:${p.targetId}`,
                label: p.label,
                role: p.role,
            })),
        [permissions],
    );

    const permissionSummary = useMemo(() => {
        const parts: string[] = [];
        for (const p of permissions) {
            if (p.target === "COMPASS_TEAM") parts.push("Compass team");
            else if (p.target === "ORGANIZATION") parts.push(p.label);
            else if (p.target === "USER") parts.push(p.label);
        }
        return parts.length > 0 ? `Shared with ${parts.join(" & ")}` : "No permissions configured";
    }, [permissions]);

    const handleDataChange = (newCols: ColumnDefinition[], newRows: DataRow[]) => {
        setColumns(newCols);
        setRows(newRows);
    };

    const handleCreate = async () => {
        if (!title.trim()) return;
        setIsCreating(true);
        try {
            const chart = await createMutation.mutateAsync({
                title: title.trim(),
                description: description.trim() || undefined,
                permissions: permissions.length
                    ? permissions.map((p) => ({
                          role: p.role,
                          target: p.target as "USER" | "ORGANIZATION" | "PUBLIC" | "COMPASS_TEAM",
                          userId: p.target === "USER" ? p.targetId : undefined,
                          organizationId: p.target === "ORGANIZATION" ? p.targetId : undefined,
                      }))
                    : undefined,
            });

            if (hasData) {
                await saveDraftMutation.mutateAsync({
                    id: chart.id,
                    data: { columns, rows },
                });
                await commitMutation.mutateAsync({
                    id: chart.id,
                    label: "Initial version",
                    defaultChartType: chartType,
                });
            }

            enqueueSnackbar("Chart created", { variant: "success" });
            navigate({ to: "/visualizations/$id", params: { id: String(chart.id) } });
        } catch {
            enqueueSnackbar("Failed to create chart", { variant: "error" });
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="py-6">
            {/* Header */}
            <div className="mb-4">
                <button
                    onClick={() => navigate({ to: "/visualizations" })}
                    className="mb-4 flex items-center text-sm text-blue-600 hover:text-blue-700"
                >
                    <svg
                        className="mr-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                    Back to Visualizations
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Create Visualization</h1>
            </div>

            <div className="flex gap-6">
                {/* Left column — Inputs */}
                <div className="min-w-0 flex-[2]">
                    <InputsCard columns={columns} rows={rows} onChange={handleDataChange} />

                    {/* Disclaimer — column type formatting */}
                    <div className="mt-3 flex rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm text-blue-600">
                        <span>
                            If a column is displaying in the wrong format (e.g. a number detected as
                            a date), click the <strong>down arrow {downArray}</strong> on the column
                            header to change its type.
                        </span>
                    </div>
                </div>

                {/* Right column — Preview, Details, Permissions */}
                <div className="w-96 shrink-0 space-y-4">
                    {hasData && (
                        <ChartPreviewCard
                            columns={columns}
                            rows={rows}
                            chartType={chartType}
                            onChartTypeChange={setChartType}
                            expandedPreview={expandedPreview}
                            onToggleExpand={() => setExpandedPreview(!expandedPreview)}
                        />
                    )}

                    <DetailsFormCard
                        title={title}
                        description={description}
                        onTitleChange={setTitle}
                        onDescriptionChange={setDescription}
                        titleRequired
                    />

                    <PermissionsSummaryCard
                        entries={permissionEntries}
                        summaryText={permissionSummary}
                        onManage={() => setShowPermissions(true)}
                        collapsible={false}
                    />

                    {/* Actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={handleCreate}
                            disabled={!title.trim() || isCreating}
                            className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isCreating ? "Creating..." : "Create Chart"}
                        </button>
                        <button
                            onClick={() => navigate({ to: "/visualizations" })}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>

            {/* Expanded Preview Overlay */}
            {expandedPreview && (
                <ExpandedPreviewOverlay
                    columns={columns}
                    rows={rows}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                    onClose={() => setExpandedPreview(false)}
                />
            )}

            {/* Permissions Modal */}
            {showPermissions && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                    onClick={() => setShowPermissions(false)}
                >
                    <div
                        className="max-h-[80vh] w-[600px] max-w-[90vw] overflow-auto rounded-xl bg-white p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="mb-4 text-lg font-semibold">Manage Permissions</h2>
                        <ChartPermissionPicker
                            value={permissions}
                            onChange={setPermissions}
                            onBeforeAdd={handleBeforeAdd}
                        />
                        <div className="mt-6 flex justify-end">
                            <Button onClick={() => setShowPermissions(false)}>Done</Button>
                        </div>
                    </div>
                </div>
            )}

            {externalOrgConfirmModal}
        </div>
    );
};
